/**
 * The leaf rows under a (possibly grouped) row list.
 *
 * Every grid's Total row is summed over these. Two reasons a total cannot simply sum the list
 * a table was handed:
 *
 *  - A grouped list holds group rows whose figures already contain their members', so summing
 *    both double-counts.
 *  - The list handed to a table is the PAGE. The server's own totals are built from its full
 *    filtered set and deliberately exclude pagination — a page is a viewport, not a selection —
 *    so a client-side total must use the same scope or it silently becomes a page subtotal that
 *    moves as you page.
 *
 * Callers therefore pass the filtered, unpaged set and flatten it here.
 *
 * Lives in its own file rather than beside the grids so it can be shared without making a
 * component module export a non-component (which breaks fast refresh).
 */
export const leafRowsOf = <T extends { subRows?: T[] }>(rows: T[]): T[] =>
    rows.flatMap((row) => (row.subRows?.length ? leafRowsOf(row.subRows) : [row]));

/**
 * Regroup a one-level order/line tree under a different key — the production grids' "group by
 * SKU", where a product's orders sit under it instead of the other way round.
 *
 * TAKES THE ROWS THE GRID ALREADY HAS, not the API response. The children it regroups are the
 * very rows the order view renders, so the two groupings cannot disagree about a figure: one is
 * a rearrangement of the other rather than a second derivation from the same source.
 *
 * A ROW WITH NO CHILDREN IS ITS OWN LEAF. Both grids flatten a headerless work order into a
 * single `kind: 'line'` row with no `lines` — it is the leaf, and `lines ?? [row]` is what keeps
 * it in the regrouping instead of silently dropping the runs commissioned without an order.
 *
 * INSERTION-ORDERED, so the result keeps the server's ordering — soonest due first — rather than
 * whatever order a Map happens to yield.
 *
 * Only the grouping is shared. Each grid builds its own parent row, because their row types
 * genuinely differ (batch codes and stock cover on one, order value and currency on the other)
 * and a common parent would be a union with half its fields null on each side.
 */
export const groupLinesBy = <T extends { lines?: T[] }>(
    rows: T[],
    keyOf: (row: T) => string,
    /**
     * Re-stamp a child with what its parent was holding on its behalf.
     *
     * Both grids leave the order's identity — its code, its id, whether it has a document — on
     * the ORDER row, because in that grouping the child sits visibly beneath it. Regrouped, the
     * child is the only place an order appears, so it has to carry those facts itself or the
     * codes vanish and the PDF link with them. A headerless row is its own parent, so it keeps
     * exactly what it had.
     */
    adopt: (line: T, parent: T) => T = (line) => line,
): Array<{ key: string; lines: T[] }> => {
    const order: string[] = [];
    const byKey = new Map<string, T[]>();

    for (const row of rows) {
        for (const line of row.lines ?? [row]) {
            const key = keyOf(line);
            if (!key) continue;

            const adopted = adopt(line, row);
            const bucket = byKey.get(key);
            if (bucket) {
                bucket.push(adopted);
            } else {
                byKey.set(key, [adopted]);
                order.push(key);
            }
        }
    }

    return order.map((key) => ({ key, lines: byKey.get(key)! }));
};
